'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cx } from '../../../internal/cx.js';
import { CloseGlyph, InfoGlyph, SuccessGlyph, WarningGlyph } from '../../../internal/icons.js';
const ToastContext = createContext(null);
/** Longer than the exit animation (--mn-duration-base). */
const EXIT_MS = 600;
const SHORT_MS = 5000;
const LONG_MS = 10000;
let counter = 0;
/**
 * Mount once near the root. Provides `useToast()` and renders the viewport.
 *
 * Toasts never take focus. They pause while hovered or focused, can be reached
 * with F8 (the `hotkey`), and close with Escape. Each one is announced once
 * through a live region: assertively for `danger`, politely otherwise.
 */
export function ToastProvider({ label = 'Notifications', position = 'bottom-right', hotkey = DEFAULT_HOTKEY, durationScale = 1, limit = 3, dismissLabel = 'Dismiss', children, }) {
    const [toasts, setToasts] = useState([]);
    const timers = useRef(new Set());
    useEffect(() => {
        const pending = timers.current;
        return () => {
            for (const t of pending)
                clearTimeout(t);
            pending.clear();
        };
    }, []);
    /** Drops closed records once the exit animation has had time to run. */
    const sweep = useCallback(() => {
        const t = setTimeout(() => {
            timers.current.delete(t);
            setToasts((list) => list.filter((r) => r.open));
        }, EXIT_MS);
        timers.current.add(t);
    }, []);
    const dismiss = useCallback((id) => {
        setToasts((list) => list.map((t) => (t.open && (id === undefined || t.id === id) ? { ...t, open: false } : t)));
        sweep();
    }, [sweep]);
    const show = useCallback((options) => {
        const id = `mn-toast-${++counter}`;
        setToasts((list) => {
            const next = [...list, { ...options, id, open: true }];
            const openOnes = next.filter((t) => t.open);
            const overflow = openOnes.length - Math.max(1, limit);
            if (overflow > 0) {
                const set = new Set(openOnes.slice(0, overflow).map((t) => t.id));
                return next.map((t) => (set.has(t.id) ? { ...t, open: false } : t));
            }
            return next;
        });
        sweep();
        return { id, dismiss: () => dismiss(id) };
    }, [dismiss, sweep, limit]);
    const toast = useMemo(() => {
        const fn = ((options) => show(options));
        const tone = (t) => (title, options) => show({ ...options, title, tone: t });
        fn.success = tone('success');
        fn.error = tone('danger');
        fn.warning = tone('warning');
        fn.info = tone('info');
        return fn;
    }, [show]);
    const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);
    return (_jsx(ToastContext.Provider, { value: value, children: _jsxs(ToastPrimitive.Provider, { swipeDirection: position.endsWith('right') ? 'right' : 'down', label: label, children: [children, toasts.map((t) => (_jsx(ToastItem, { record: t, durationScale: durationScale, dismissLabel: dismissLabel, onOpenChange: (open) => (open ? undefined : dismiss(t.id)) }, t.id))), _jsx(ToastPrimitive.Viewport, { className: "mn-toast-viewport", "data-position": position, hotkey: hotkey, label: hotkey.length ? `${label} ({hotkey})` : label })] }) }));
}
const DEFAULT_HOTKEY = ['F8'];
const toneIcon = {
    neutral: null,
    info: _jsx(InfoGlyph, {}),
    success: _jsx(SuccessGlyph, {}),
    warning: _jsx(WarningGlyph, {}),
    danger: _jsx(WarningGlyph, {}),
};
/** The duration Radix should use: scaled, and Infinity when auto-dismiss is off. */
function toastDuration(record, scale) {
    const base = record.duration ?? (record.tone === 'danger' || record.action ? LONG_MS : SHORT_MS);
    const scaled = base * (scale > 0 ? scale : 1);
    return Number.isFinite(scaled) ? Math.round(scaled) : Infinity;
}
function ToastItem({ record, durationScale, dismissLabel, onOpenChange, }) {
    const titleId = useId();
    const tone = record.tone ?? 'neutral';
    const politeness = record.politeness ?? (tone === 'danger' ? 'assertive' : 'polite');
    const icon = toneIcon[tone];
    // onDismiss fires once, the first time the record closes.
    const notified = useRef(false);
    const onDismiss = record.onDismiss;
    useEffect(() => {
        if (!record.open && !notified.current) {
            notified.current = true;
            onDismiss?.();
        }
    }, [record.open, onDismiss]);
    return (
    // No role on the <li>: it must stay a list item of the viewport's <ol>.
    // Radix announces the text through its own live region, whose politeness
    // follows `type` (foreground = assertive, background = polite).
    _jsxs(ToastPrimitive.Root, { open: record.open, onOpenChange: onOpenChange, duration: toastDuration(record, durationScale), type: politeness === 'assertive' ? 'foreground' : 'background', className: "mn-toast", "data-tone": tone, "data-politeness": politeness, "aria-labelledby": titleId, children: [icon ? (_jsx("span", { className: "mn-toast__icon", "aria-hidden": true, children: icon })) : null, _jsxs("div", { className: "mn-toast__body", children: [_jsx(ToastPrimitive.Title, { id: titleId, className: "mn-toast__title", children: record.title }), record.description ? (_jsx(ToastPrimitive.Description, { className: "mn-toast__description", children: record.description })) : null] }), record.action ? (_jsx(ToastPrimitive.Action, { asChild: true, altText: record.action.altText || record.action.label, children: _jsx("button", { type: "button", className: cx('mn-button', 'mn-toast__action'), "data-variant": "secondary", "data-size": "xs", onClick: record.action.onClick, children: record.action.label }) })) : null, _jsx(ToastPrimitive.Close, { className: "mn-toast__close", "aria-label": dismissLabel, "aria-describedby": titleId, children: _jsx(CloseGlyph, {}) })] }));
}
/**
 * Returns `toast` (callable, with `.success`, `.error`, `.warning`, `.info`)
 * and `dismiss(id?)`. Must be used inside ToastProvider.
 */
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx)
        throw new Error('useToast must be used inside <ToastProvider>');
    return ctx;
}
/** Messages that describe the transport, not the problem; replaced by the fallback. */
const GENERIC = [
    /^request failed with status code \d{3}$/i,
    /^network ?error$/i,
    /^failed to fetch$/i,
    /^load failed$/i,
    /^networkerror when attempting to fetch resource\.?$/i,
    /^internal server error$/i,
    /^\[object object\]$/i,
    // An HTML error page from a proxy or gateway.
    /^<(!doctype|html|head|body)\b/i,
];
function text(value) {
    if (typeof value === 'string') {
        const t = value.trim();
        return t && !GENERIC.some((re) => re.test(t)) ? t : undefined;
    }
    if (Array.isArray(value)) {
        const parts = value
            .map((v) => text(typeof v === 'object' && v
            ? (v.message ?? v.detail)
            : v))
            .filter(Boolean);
        return parts.length ? parts.join(' ') : undefined;
    }
    return undefined;
}
function fromBody(body) {
    if (!body || typeof body !== 'object')
        return text(body);
    const b = body;
    const details = b.details;
    const fields = (details?.fields ?? b.fields ?? b.fieldErrors);
    if (fields && typeof fields === 'object') {
        const flat = Object.values(fields).flatMap((v) => (Array.isArray(v) ? v : [v]));
        const joined = text(flat);
        if (joined)
            return joined;
    }
    return text(b.message) ?? text(b.errors) ?? text(b.detail) ?? text(b.error) ?? text(b.title);
}
/**
 * Turns anything thrown or rejected into one sentence a person can read, for
 * `toast.error()` or an inline error. Pure; it knows no product's API.
 *
 * Looks, in order, at: a string; field errors (`details.fields`, `fields`,
 * `fieldErrors`, flattened); `message` (string or list); `errors[]`
 * (`message`/`detail`); `detail`; `error`; `title`, on the response body
 * (`error.response.data`, `error.data`, `error.body`) and then the error
 * itself. Transport messages ("Request failed with status code 500",
 * "Failed to fetch") are never shown; the fallback is.
 */
export function getErrorMessage(error, options = {}) {
    const fallback = options.fallback ?? 'Something went wrong. Try again.';
    if (error == null)
        return fallback;
    if (typeof error === 'string')
        return text(error) ?? fallback;
    if (typeof error !== 'object')
        return fallback;
    const e = error;
    const response = e.response;
    const found = fromBody(response?.data) ?? fromBody(e.data) ?? fromBody(e.body) ?? fromBody(e);
    if (found)
        return found;
    const raw = typeof e.message === 'string' ? e.message.trim() : '';
    const isNetwork = e.name === 'TypeError' ||
        e.code === 'ERR_NETWORK' ||
        /^(network ?error|failed to fetch|load failed|networkerror)/i.test(raw);
    if (isNetwork && options.network)
        return options.network;
    return fallback;
}
//# sourceMappingURL=toast.js.map